import { useState } from "react";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import {
  AccountAdmincontent,
  AccountAdminFrame,
  AccountAdminHead,
  AccountAdminHolder,
  AccountAdminsidebar,
  AccountAdmintitle,
  BotIconText,
  ContentBox,
  TableUsage,
  TypographyHeding,
  TypographyHedingBig,
} from "./style";

import { useQuery } from "@apollo/client";
import { Typography } from "@mui/material";
import { useParams } from "react-router-dom";
import { BasicPage } from "../common/BasicPage/BasicPage";
import { BrightBotHomeIcon } from "../common/Icons/BrightBotHomeIcon";
import { GetUsageDocument } from "../generated";
import { formatDateMonthDD, formatDateYYYMMDD } from "../helpers";
import CustomDateTick from "./components/CustomDateTick";
import useUsage from "./hooks/useUsage";

const endDate = new Date(); // Create a copy of the current date
const startDate = new Date(endDate); // Current date
startDate.setDate(endDate.getDate() - 29); // Subtract 30 days

export const AccountAdmin = () => {
  const { workspaceId } = useParams();
  const [showBreakdown, setBreakdown] = useState(false);

  const { data, loading } = useQuery(GetUsageDocument, {
    variables: {
      input: {
        workspaceId: workspaceId || "",
      },
      usageFilter: {
        endDate: formatDateYYYMMDD(endDate),
        startDate: formatDateYYYMMDD(startDate),
      },
    },
  });

  const usageExist = data?.workspace?.usage?.totalUsage?.length > 0;

  const { formatYAxis, usageData, monthlyUsers, maxUsageValue } = useUsage({
    startDate,
    endDate,
    data,
  });

  return (
    <>
      <BasicPage
        leftHeaderElements={<></>}
        content={
          <AccountAdminHolder>
            <AccountAdminFrame>
              <AccountAdmincontent>
                {/* Monthly Spend */}
                <ContentBox>
                  <AccountAdminHead>
                    <TypographyHeding variant="h6" className="small">
                      Monthly Usage
                    </TypographyHeding>
                    <AccountAdmintitle>
                      {`${formatDateMonthDD(startDate)} - ${formatDateMonthDD(
                        endDate
                      )}`}
                    </AccountAdmintitle>
                  </AccountAdminHead>
                  {(!usageExist || usageData?.length === 0) && !loading ? (
                    <>
                      <Typography
                        variant="body3"
                        className="text-center"
                        sx={{ display: "block" }}
                      >
                        No usage data available for the selected period
                      </Typography>
                    </>
                  ) : (
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart data={usageData}>
                        <XAxis dataKey="date" tick={<CustomDateTick />} />
                        <YAxis
                          tickFormatter={formatYAxis as any}
                          type="number"
                          domain={[0, maxUsageValue * 1.1]}
                          allowDataOverflow={true}
                          tickCount={6}
                        />
                        <Tooltip
                          formatter={(value: number) => formatYAxis(value)}
                        />
                        <Bar dataKey="tokens" fill="#14b8a6" />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                  {/* <Button
              onClick={() => setBreakdown(!showBreakdown)}
              className="mt-4"
            >
              View Breakdown{" "}
              {showBreakdown ? <ArrowUpIcon /> : <ArrowDownIcon />}
            </Button>
            {showBreakdown && <UsageBreakdown />} */}
                </ContentBox>
              </AccountAdmincontent>
              <AccountAdminsidebar>
                {/* Monthly Usage Breakdown */}
                <ContentBox>
                  <AccountAdminHead className="add">
                    <TypographyHeding variant="h6" className="small">
                      Monthly Usage Breakdown
                    </TypographyHeding>
                    <AccountAdmintitle className="add">
                      {`${formatDateMonthDD(startDate)} - ${formatDateMonthDD(
                        endDate
                      )}`}
                    </AccountAdmintitle>
                  </AccountAdminHead>

                  {(!usageExist || usageData?.length === 0) && !loading ? (
                    <>
                      <Typography
                        variant="body3"
                        className="text-center"
                        sx={{ display: "block" }}
                      >
                        No usage data available for the selected period
                      </Typography>
                    </>
                  ) : (
                    <ResponsiveContainer width="100%" height={300}>
                      <AreaChart data={usageData}>
                        <XAxis dataKey="date" tick={<CustomDateTick />} />
                        <YAxis
                          tickFormatter={formatYAxis as any}
                          type="number"
                          domain={[0, maxUsageValue * 1.1]}
                          allowDataOverflow={true}
                          tickCount={6}
                        />
                        <Tooltip
                          formatter={(value: number) => formatYAxis(value)}
                        />
                        <Area
                          type="monotone"
                          dataKey="tokens"
                          stroke="#3b82f6"
                          fill="#3b82f6"
                          fillOpacity={0.3}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  )}
                  <BotIconText>
                    <BrightBotHomeIcon /> BrightAgent (Tokens)
                  </BotIconText>
                </ContentBox>
                {/* Monthly Users */}
                <ContentBox>
                  <TypographyHedingBig variant="h6">
                    Monthly Usage
                  </TypographyHedingBig>
                  <AccountAdmintitle className="more">
                    <b>{monthlyUsers?.length || 0}</b> Active Users
                  </AccountAdmintitle>
                  <TableUsage>
                    <div className="table-holder">
                      <table>
                        <thead>
                          <tr>
                            <th>User</th>
                            <th>Credits Used</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(!usageExist || usageData?.length === 0) &&
                          !loading ? (
                            <tr>
                              <td style={{ textAlign: "center" }} colSpan={2}>
                                <Typography
                                  variant="body3"
                                  className="text-center"
                                  sx={{ display: "block" }}
                                >
                                  No monthly usage available for the selected
                                  period
                                </Typography>
                              </td>
                            </tr>
                          ) : (
                            <>
                              {monthlyUsers?.map((user: any, index) => (
                                <tr key={`${user.userId}-${index}`}>
                                  <td key={user.userId}>
                                    <span>
                                      {user.firstName} {user.lastName}
                                    </span>
                                  </td>
                                  <td>
                                    {/* {user.usage.toLocaleString()} credits - */}
                                    {formatYAxis(user.tokens)}
                                  </td>
                                </tr>
                              ))}
                            </>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </TableUsage>
                  {/* <div>
              <Button className="m-auto block">View Old Records</Button>
            </div> */}
                </ContentBox>
              </AccountAdminsidebar>
            </AccountAdminFrame>{" "}
          </AccountAdminHolder>
        }
      />
    </>
  );
};
